import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import LazyTooltip from 'components/LazyTooltip';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useMemo } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { InvoiceExpense, InvoiceMatterForm } from 'types/invoice-details';
import { EditIntentTypeCode } from 'types/invoices';
import { t } from 'utils/getTranslations';

type Props = {
    editIntentTypeCode: string | null;
    index: number;
    invoiceExpense: InvoiceExpense;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    control: Control<InvoiceMatterForm>;
    handleUpdateInvoiceExpenses: (idList: number[]) => void;
};

export const TakeActionBackDisplay = ({
    index,
    invoiceExpense,
    editIntentTypeCode,
    setValue,
    control,
    handleUpdateInvoiceExpenses,
}: Props) => {
    const [newMatter] = useWatch({ control, name: [`invoiceExpenses.${index}.newMatter`] });
    const { buttonsDisabled } = useSelector((state) => state.buttons);

    const invoiceExpenseChangedText = useMemo(() => {
        switch (editIntentTypeCode) {
            case EditIntentTypeCode.REMOVE_VIA_CORRECTION: {
                return t('invoices.details.table.expense-is-credited');
            }
            case EditIntentTypeCode.MOVE_TO_ANOTHER_CASE: {
                return t('invoices.details.table.expense-is-moved');
            }
            case EditIntentTypeCode.MARK_AS_NONBILLABLE: {
                return t('invoices.details.table.expense-is-non-billable');
            }
            case EditIntentTypeCode.POSTPONE: {
                return t('invoices.details.table.expense-is-postponed');
            }
            default: {
                return '';
            }
        }
    }, [editIntentTypeCode]);

    const invoiceExpenseTooltipText = useMemo(() => {
        switch (editIntentTypeCode) {
            case EditIntentTypeCode.REMOVE_VIA_CORRECTION: {
                return t('invoices.details.table.expense-is-credited-tooltip');
            }
            case EditIntentTypeCode.MOVE_TO_ANOTHER_CASE: {
                return t('invoices.details.table.expense-is-moved-tooltip');
            }
            case EditIntentTypeCode.MARK_AS_NONBILLABLE: {
                return t('invoices.details.table.expense-is-non-billable-tooltip');
            }
            case EditIntentTypeCode.POSTPONE: {
                return t('invoices.details.table.expense-is-postponed-tooltip');
            }
            default: {
                return '';
            }
        }
    }, [editIntentTypeCode]);

    const handleToTakeActionBack = () => {
        if (editIntentTypeCode === EditIntentTypeCode.MOVE_TO_ANOTHER_CASE) {
            setValue(`invoiceExpenses.${index}.newMatter`, null, {
                shouldDirty: true,
            });
        } else if (editIntentTypeCode === EditIntentTypeCode.MARK_AS_NONBILLABLE) {
            setValue(`invoiceExpenses.${index}.isNonBillable`, false, {
                shouldDirty: true,
            });
        }
        setValue(`invoiceExpenses.${index}.editIntentTypeCode`, null, { shouldDirty: true });
        handleUpdateInvoiceExpenses([invoiceExpense.id]);
    };

    return (
        <div className="pointer-events-auto mb-2 opacity-100">
            <div>
                <p className="text-body2 text-fg-error flex items-center">
                    {invoiceExpenseChangedText}
                    <LazyTooltip title={invoiceExpenseTooltipText} placement="bottom">
                        <InfoOutlinedIcon className="text-fg-secondary ml-[1px] h-[1.2rem]" />
                    </LazyTooltip>
                    <button
                        type="button"
                        onClick={handleToTakeActionBack}
                        disabled={buttonsDisabled}
                        className="text-fg hover:text-link cursor-pointer underline disabled:pointer-events-none disabled:opacity-50"
                    >
                        <FormattedMessage id="invoices.actions.take-back" />
                    </button>
                </p>
            </div>
            <p className="text-body2">
                {newMatter ? (
                    <RecordLink
                        to={`/matter/${newMatter.id}`}
                        className="text-fg hover:text-link w-fit no-underline hover:underline"
                        label={newMatter.name}
                        withOrigin
                    />
                ) : null}
            </p>
        </div>
    );
};
